import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Loader2, Plus, Trash2, Copy, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMAGES = ["clim-salon", "clim-famille", "clim-exterieur", "clim-pose"];

export default function AdsGenerator({ token }) {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [ads, setAds] = useState([]);
  const [saved, setSaved] = useState([]);
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const base = `${window.location.origin}/climatisation?v=`;

  const load = () =>
    axios.get(`${API}/variants?product=clim`).then(({ data }) => setSaved(data.variants || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/ads/generate`, { product: "clim", brief, count: 5 }, auth);
      setAds(data.ads);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Génération impossible.");
    } finally {
      setBusy(false);
    }
  };

  const save = async (ad) => {
    try {
      await axios.post(`${API}/admin/variants`, { ...ad, image: IMAGES.includes(ad.image) ? ad.image : "clim-salon", product: "clim" }, auth);
      toast.success(`Variante ?v=${ad.key} ajoutée`);
      setAds((a) => a.filter((x) => x.key !== ad.key));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible.");
    }
  };

  const remove = async (key) => {
    try {
      await axios.delete(`${API}/admin/variants/${key}?product=clim`, auth);
      toast.success("Variante supprimée");
      load();
    } catch (e) {
      toast.error("Suppression impossible.");
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success("Copié"));

  return (
    <div data-testid="admin-ads-generator" className="mt-4 rounded-2xl border border-white/10 bg-forest-surface/60 px-5 py-4">
      <button data-testid="admin-ads-toggle" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-3 text-left">
        <p className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-violet-300">
          <Sparkles className="h-3.5 w-3.5" /> Générateur d'accroches Meta (IA Claude) — {saved.length} variante{saved.length > 1 ? "s" : ""} personnalisée{saved.length > 1 ? "s" : ""}
        </p>
        <ChevronDown className={`h-4 w-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-4 space-y-5">
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              data-testid="admin-ads-brief"
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Brief facultatif : ex. cibler les familles avec enfants, insister sur le silence la nuit…"
              className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-violet-400"
            />
            <button data-testid="admin-ads-generate" onClick={generate} disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-violet-400 px-5 py-2.5 text-sm font-bold text-forest-deep transition-all hover:bg-violet-300 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Générer 5 accroches
            </button>
          </div>

          {ads.length > 0 && (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-ads-results">
              {ads.map((ad) => (
                <div key={ad.key} data-testid={`admin-ad-${ad.key}`} className="flex flex-col rounded-xl border border-violet-400/20 bg-white/5 p-4">
                  <p className="font-mono text-[10px] text-violet-300">{ad.label} · ?v={ad.key} · {ad.image}</p>
                  <p className="mt-2 font-display text-lg font-extrabold leading-tight text-white">
                    {ad.lines.map((l, i) => (<span key={i} className={`block ${i === ad.lines.length - 1 ? "text-sky-300" : ""}`}>{l}</span>))}
                  </p>
                  <p className="mt-2 flex-1 text-xs text-white/60">{ad.sub}</p>
                  <button data-testid={`admin-ad-save-${ad.key}`} onClick={() => save(ad)} className="mt-3 flex items-center justify-center gap-1.5 rounded-full border border-violet-400/40 px-3 py-1.5 text-xs font-bold text-violet-200 hover:bg-violet-400/10">
                    <Plus className="h-3.5 w-3.5" /> Ajouter comme variante
                  </button>
                </div>
              ))}
            </div>
          )}

          {saved.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-white/35">Variantes en ligne</p>
              <ul className="mt-2 divide-y divide-white/5 rounded-xl border border-white/10">
                {saved.map((v) => (
                  <li key={v.key} data-testid={`admin-variant-saved-${v.key}`} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-xs">
                    <span className="font-display text-sm font-bold text-white">{v.lines.join(" ")}</span>
                    <a href={base + v.key} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-sky-300 underline underline-offset-2">/climatisation?v={v.key}</a>
                    <span className="ml-auto flex items-center gap-1">
                      <button onClick={() => copy(base + v.key)} title="Copier l'URL" className="grid h-7 w-7 place-items-center rounded-lg border border-white/10 text-white/50 hover:text-white"><Copy className="h-3 w-3" /></button>
                      <button data-testid={`admin-variant-delete-${v.key}`} onClick={() => remove(v.key)} title="Supprimer" className="grid h-7 w-7 place-items-center rounded-lg border border-white/10 text-white/50 hover:border-red-400/60 hover:text-red-300"><Trash2 className="h-3 w-3" /></button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
